import { useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from 'api/api'
import { ApiError } from 'api/ApiError'
import { OfferResponse } from 'api/gen'
import { OfferNotFound } from 'features/offer/pages/OfferNotFound/OfferNotFound'
import { useLogTypeFromRemoteConfig } from 'libs/hooks/useLogTypeFromRemoteConfig'
import { LogTypeEnum, OfferNotFoundError } from 'libs/monitoring/errors'
import { QueryKeys } from 'libs/queryKeys'

const getOfferById = async (offerId: number, logType: LogTypeEnum) => {
  if (!offerId) {
    throw new OfferNotFoundError(offerId, {
      Screen: OfferNotFound,
      logType,
    })
  }
  try {
    return await api.getNativeV3OfferofferId(offerId)
  } catch (error) {
    if (error instanceof ApiError && error.statusCode === 404) {
      // This happens when the offer has been rejected but it is still indexed on Algolia
      // due to asynchronous reindexing of the back office
      throw new OfferNotFoundError(offerId, {
        Screen: OfferNotFound,
        logType,
      })
    }
    throw error
  }
}

export const useOfferQuery = <T = OfferResponse>({
  offerId,
  select,
}: {
  offerId: number
  select?: (data: OfferResponse) => T | undefined
}) => {
  const { logType } = useLogTypeFromRemoteConfig()
  const queryClient = useQueryClient()

  return useQuery({
    queryKey: [QueryKeys.OFFER, offerId],
    queryFn: async () => {
      const offer = await getOfferById(offerId, logType)
      queryClient.setQueryData([QueryKeys.OFFER, QueryKeys.PREVIEW, offerId], false)
      return offer
    },
    enabled: !!offerId,
    select,
  })
}
